import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionProvider } from "@/lib/motion";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import { PremiumCtaBanner } from "./components/sections/PremiumCtaBanner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MotionOrchestrator } from "./contexts/MotionContext";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load all routes for code splitting
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Work = lazy(() => import("./pages/Work"));
const WorkDetail = lazy(() => import("./pages/WorkDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const About = lazy(() => import("./pages/About"));
const Partners = lazy(() => import("./pages/Partners"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const HowWeWork = lazy(() => import("./pages/HowWeWork"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Comparisons = lazy(() => import("./pages/Comparisons"));
const ComparisonDetail = lazy(() => import("./pages/ComparisonDetail"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-sand/20 border-t-sand rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <ThemeProvider>
          <MotionOrchestrator>
            <MotionProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ErrorBoundary>
                  <div className="min-h-screen flex flex-col relative z-10">
                    <Header />
                    <main className="flex-1 pt-16 md:pt-20">
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/work" element={<Work />} />
                            <Route path="/work/:slug" element={<WorkDetail />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogDetail />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/partners" element={<Partners />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/faq" element={<Faq />} />
                            <Route path="/how-we-work" element={<HowWeWork />} />
                            <Route path="/resources" element={<Resources />} />
                            <Route path="/resources/:slug" element={<ResourceDetail />} />
                            <Route path="/comparisons" element={<Comparisons />} />
                            <Route path="/comparisons/:slug" element={<ComparisonDetail />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </ErrorBoundary>
                    </main>
                    <PremiumCtaBanner />
                    <Footer />
                  </div>
                </ErrorBoundary>
              </BrowserRouter>
        </MotionProvider>
          </MotionOrchestrator>
        </ThemeProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
